import { z } from 'zod';

import { enforceRangePairs } from '@/lib/api/filter-spec';
import { pageMetaSchema } from '@/lib/api/pagination';

import { REPORT_RANGE_ERROR } from '../constants';

/** One row = one owned, **subscribable** Device unit (CONTEXT.md "row grain"). Components and units
 *  superseded by an approved replacement are excluded by the backend, not here.
 *
 *  The four telemetry fields return `0/0/0/null` on every row until a telemetry domain exists — a
 *  state the FE cannot tell apart from the AC's legitimate never-active Device, which is why nothing
 *  branches on them (spec.md "Device Activity data availability").
 *
 *  `categoryName` is nullable defensively: the stub always sends a string, but the Warranty queue
 *  found the equivalent field nullable on the real contract, and a parse failure blanks the grid. */
export const deviceActivityRowSchema = z.object({
  id: z.string(),
  // Not rendered — carried for ticket 07's Customer filter and for row identity debugging.
  customerId: z.string(),
  customerName: z.string(),
  /** Raw digits; `formatPhoneUS` formats at the cell (ADR-0020). */
  phone: z.string(),
  email: z.string().nullable(),
  deviceName: z.string(),
  categoryName: z.string().nullable(),
  /** Cumulative hours from the Device's first active day. */
  totalActiveTime: z.number(),
  lastActiveOn: z.string().nullable(),
  /** Hours; decimal, because the metric is a division. */
  avgRuntimePerDay: z.number(),
  totalAlerts: z.number(),
  /** `YYYY-MM-DD` — the containing order's placed date, not the delivery date. */
  purchaseDate: z.string(),
});

export const deviceActivityListResponseSchema = z.object({
  data: z.array(deviceActivityRowSchema),
  meta: pageMetaSchema,
});

/** RHF draft for the filter drawer (README AC1). Multi-selects are always arrays; every numeric
 *  bound and both date bounds are `null` when unset — antd's clearable controls clear to `null`,
 *  and an `undefined` RHF value silently reverts to `defaultValues`.
 *
 *  The three From/To guards reuse ticket 01's `enforceRangePairs` with the one shared
 *  `REPORT_RANGE_ERROR` string, so the AC's exact copy has a single home across all seven Reports
 *  ranges. The purchase-date pair is deliberately **not** guarded: `<DateRangePicker>` disables
 *  dates before the From for the To end, so it cannot emit an inverted range (plan D3).
 *
 *  No `.min(0)`: `<NumberField min={0}>` already makes a negative unenterable, and no AC supplies
 *  an error string for one. */
export const deviceActivityFiltersSchema = z
  .object({
    customerIds: z.array(z.string()),
    deviceIds: z.array(z.string()),
    categoryIds: z.array(z.string()),
    totalActiveTimeFrom: z.number().nullable(),
    totalActiveTimeTo: z.number().nullable(),
    avgRuntimePerDayFrom: z.number().nullable(),
    avgRuntimePerDayTo: z.number().nullable(),
    totalAlertsFrom: z.number().nullable(),
    totalAlertsTo: z.number().nullable(),
    purchaseDateFrom: z.string().nullable(),
    purchaseDateTo: z.string().nullable(),
  })
  .superRefine(
    enforceRangePairs(
      [
        ['totalActiveTimeFrom', 'totalActiveTimeTo'],
        ['avgRuntimePerDayFrom', 'avgRuntimePerDayTo'],
        ['totalAlertsFrom', 'totalAlertsTo'],
      ],
      REPORT_RANGE_ERROR,
    ),
  );
